import React from "react";
import {useTranslation} from "react-i18next";

const isObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const valueAtPath = (record: Record<string, any>, path: string) =>
  path.split(".").reduce<any>((value, part) => value == null ? undefined : value[part], record);

export const parsedStructuredString = (value: unknown) => {
  if (typeof value !== "string" || !/^\s*[\[{]/.test(value)) return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) || isObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const leafPaths = (value: unknown, prefix = "", depth = 0): string[] => {
  if (!isObject(value) || depth >= 5) return prefix ? [prefix] : [];
  const entries = Object.entries(value);
  if (!entries.length) return prefix ? [prefix] : [];
  return entries.flatMap(([key, nested]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    return isObject(nested) ? leafPaths(nested, path, depth + 1) : [path];
  });
};

const primitiveText = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "✓" : "—";
  return String(value);
};

const compactSummary = (value: unknown) => {
  if (Array.isArray(value)) return `${value.length}`;
  if (isObject(value)) return `${Object.keys(value).length}`;
  return "";
};

export default function StructuredValue({value, compact = false}: {value: unknown; compact?: boolean}) {
  const {t} = useTranslation();
  const parsed = parsedStructuredString(value);
  if (parsed) return <StructuredValue value={parsed} compact={compact}/>;
  const label = (key: string) => t(`dataTable.fields.${key}.label`, {
    defaultValue: key.replaceAll("_", " ").replace(/([a-z])([A-Z])/g, "$1 $2"),
  });

  if (!Array.isArray(value) && !isObject(value)) {
    return <span className="structured-primitive">{primitiveText(value)}</span>;
  }

  if (compact) {
    const items = Array.isArray(value)
      ? value.slice(0, 3).map((item, index) => [String(index + 1), item] as const)
      : Object.entries(value).slice(0, 3);
    return <span className="structured-compact" title={t("dataTable.nestedItems", {count: Number(compactSummary(value))})}>
      {items.map(([key, item]) => <span className="structured-chip" key={key}>
        {!Array.isArray(value) && <b>{label(key)}</b>}
        <span>{Array.isArray(item) || isObject(item) ? t("dataTable.nestedItems", {count: Number(compactSummary(item))}) : primitiveText(item)}</span>
      </span>)}
      {Number(compactSummary(value)) > 3 && <span className="structured-more">+{Number(compactSummary(value)) - 3}</span>}
    </span>;
  }

  if (Array.isArray(value)) {
    if (!value.length) return <span className="structured-empty">—</span>;
    return <ol className="structured-list">
      {value.map((item, index) => <li key={index}><StructuredValue value={item}/></li>)}
    </ol>;
  }

  const entries = Object.entries(value);
  if (!entries.length) return <span className="structured-empty">—</span>;
  return <dl className="structured-object">
    {entries.map(([key, nested]) => <div key={key}>
      <dt title={key}>{label(key)}</dt>
      <dd><StructuredValue value={nested}/></dd>
    </div>)}
  </dl>;
}
